import React, { useMemo, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';

const ZoneExplorerTabButton = ({ active, onClick, children }) => (
  <Button
    size="sm"
    variant="outline-light"
    onClick={onClick}
    className={`px-3 zone-explorer-tab-btn ${active ? 'zone-explorer-tab-btn--active' : ''}`}
  >
    {children}
  </Button>
);

const zoneName = (zone) => zone?.name || `Zone #${zone?.id ?? '?'}`;

export default function ZoneLinksPanel({
  zones = [],
  activeZoneId = null,
  zoneExplorerMode = 'links',
  onSetZoneExplorerMode = () => {},
  onSelectZone = () => {},
}) {
  const [search, setSearch] = useState('');

  const filteredZones = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return Array.isArray(zones) ? zones : [];
    return (Array.isArray(zones) ? zones : []).filter((zone) => {
      if (zoneName(zone).toLowerCase().includes(query)) return true;
      const tours = Array.isArray(zone?.tours) ? zone.tours : [];
      return tours.some((tour) => (tour?.name || '').toLowerCase().includes(query));
    });
  }, [search, zones]);

  return (
    <div className="card border-0 shadow-lg text-white h-100">
      <div className="card-body d-flex flex-column p-2" style={{ minHeight: 0 }}>
        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
          <div className="d-flex flex-column gap-1">
            <div className="zone-panel-heading-copy">
              <h6 className="mb-0">Zone Links</h6>
              <div className="text-muted small">Zones linked to tours</div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'list'}
                onClick={() => onSetZoneExplorerMode('list')}
              >
                Zone List
              </ZoneExplorerTabButton>
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'tree'}
                onClick={() => onSetZoneExplorerMode('tree')}
              >
                Zone Tree
              </ZoneExplorerTabButton>
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'links'}
                onClick={() => onSetZoneExplorerMode('links')}
              >
                Zone Links
              </ZoneExplorerTabButton>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg="secondary">{filteredZones.length}</Badge>
          </div>
        </div>

        <div className="zone-list-search-wrap">
          <Form.Control
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search zones or tours..."
            size="sm"
          />
        </div>

        <div
          className="zone-list-items flex-grow-1 overflow-auto d-flex flex-column gap-1"
          style={{ minHeight: 0 }}
        >
          {filteredZones.length ? (
            filteredZones.map((zone) => {
              const idKey = `${zone?.id}`;
              const isActive = activeZoneId != null && `${activeZoneId}` === idKey;
              const tours = Array.isArray(zone?.tours) ? zone.tours : [];
              return (
                <div
                  key={idKey}
                  className={`zone-selection-row responder-interactive-item ${
                    isActive ? 'is-selected' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="zone-selection-item zone-selection-item-btn d-flex flex-column align-items-start gap-1"
                    onClick={() => onSelectZone(zone)}
                  >
                    <div className="d-flex w-100 align-items-center justify-content-between gap-2">
                      <span className="fw-semibold">{zoneName(zone)}</span>
                      <Badge bg="secondary">{tours.length}</Badge>
                    </div>
                    <span className="small text-muted">
                      {tours.length
                        ? tours
                            .slice(0, 2)
                            .map((tour) => tour?.name || `Tour #${tour?.id ?? '?'}`)
                            .join(', ')
                        : 'No linked tours'}
                      {tours.length > 2 ? ' ...' : ''}
                    </span>
                  </button>
                </div>
              );
            })
          ) : (
            <div className="text-muted small">No linked zones match your search.</div>
          )}
        </div>
      </div>
    </div>
  );
}
